import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChatComposerLayout } from "@photon/chat-react/chat-layout";
import { Mic, MicActive } from "../../assets";
import logger from "../../services/Logger";
import PromptStore from "../../store/promptStore";
import { useGetSkills } from "../../api/queryHooks";
import { FileStore } from "../../store/fileUploadStore";
import { DeleteUploadedFile } from "../../utils/sparkApi/deleteFileFromDb";
import UserAuthStore from "../../store/userStore";
import { handleFileUpload } from "../../utils/fileUploadHandler";
import { updateRecaching } from "../../utils/sparkApi/updateRecaching";
import { invalidateChatDataCache } from "../../utils/sparkApi/getCacheandFileData";
import AttachDropdown from "../Dropdowns/AttachDropdown";
import PromptModelSelector from "./PromptModelSelector";
import PromptDeviceSelector from "./PromptDeviceSelector";
import PromptSkillSelector from "./PromptSkillSelector";
import PromptStyleSelector from "./PromptStyleSelector";
import WebSearchToggle from "./WebSearchToggle";
import FileUploadItem from "./FileUploadItem";
import "../../assets/css/prompt.css";
import useFeedback from "../../hooks/useFeedback";

import ChatStore from "../../store/chatStore";
import addChat from "../../utils/sparkApi/addChat";
import { uploadFiles } from "../../utils/sparkApi/fileUpload";
import { useSparkDeletion } from "../../hooks/chat/useSparkDeletion";


import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import { ArrowUp, Wand2, X } from "lucide-react";
import MemoryChip from "../Spark/MemoryChip/MemoryChip";
import CustomTooltip from "../Tooltip/CustomTooltip";
import { ALLOWED_FILE_ACCEPT_STRING } from "../../utils/helpers";
import StopGenerationButton from "./StopGenerationButton";
import { useSparkContext } from "../../context/SparkContext";
import useHeaderSelectedAIModel from "../../store/useHeaderSelectedAIModel";
import { getFilesModelCompatibilityError } from "../../utils/fileModelCompatibility";

const Prompt = ({
  chat,
  send,
  generateStatus,
  setGenerateStatus,
  setRegenerateIndex,
  messageLength = 0,
  isNewDrag,
  setIsNewDrag,
  agent,
}) => {
  const { showFeedback } = useFeedback();
  const { chat_id, space, isNewChat, isNewChatWithSpace } = useSparkContext();
  const prompt = PromptStore((state) => state.prompt);
  const activeSkills = PromptStore((state) => state.activeSkills);
  const { setPrompt } = PromptStore.getState();

  const [micActive, setMicActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [promptOverflow, setPromptOverflow] = useState({ top: false, bottom: false });
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  const files = FileStore((state) => state.files) || [];
  const fileLoading = FileStore((state) => state.fileLoading);
  const new_chat_files = FileStore((state) => state.new_chat_files) || [];
  const setFiles = FileStore((state) => state.setFiles);
  const setNewFiles = FileStore((state) => state.setNewFiles);
  const { user, orgId } = UserAuthStore.getState();
  const [uploadingFiles, setUploadingFiles] = useState({});
  const selectedModel = useHeaderSelectedAIModel((state) => state.selectedModel);

  const { data: skillsData } = useGetSkills(orgId, { enabled: !!orgId });
  const availableSkills = useMemo(
    () => (skillsData?.skills || []).filter((s) => s.enabled && s.scope === 'all'),
    [skillsData]
  );

  const toggleSkill = (skillId) => {
    const { setActiveSkills } = PromptStore.getState();
    const current = PromptStore.getState().activeSkills;
    setActiveSkills(
      current.includes(skillId)
        ? current.filter((id) => id !== skillId)
        : [...current, skillId]
    );
  };
  const setTempChatId = ChatStore((state) => state.setTempChatId);

  const generate_id = useGenerateApiSignal((state) => state.signals[chat_id]?.generate_id);

  const { deleteSpark } = useSparkDeletion(space);

  const removeFile = async (file) => {
    updateRecaching(chat_id, true);
    invalidateChatDataCache(chat_id);

    setUploadingFiles((prev) => {
      const newState = { ...prev };
      delete newState[file.name];
      return newState;
    });

    try {
      file?.uploadTask?.cancel();
    } catch (error) {
      logger.warn('Prompt', 'Upload task cancel failed or was undefined', { error: error.message });
    }
    setFiles([...files.filter((f) => f?.file__path !== file?.file__path)]);
    try {
      await DeleteUploadedFile(file?.file__path, chat_id, file, space);
    } catch (err) {
      logger.error('Prompt', 'Error deleting file from DB', { error: err.message, chat_id });
    }
    fileInputRef.current.value = "";
  };

  const removeFile2 = async (file__path) => {
    setNewFiles([
      ...new_chat_files.filter((f) => f?.file__path !== file__path),
    ]);
  };

  // Auto-focus prompt textarea when a new chat is created
  useEffect(() => {
    if ((isNewChat || isNewChatWithSpace) && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isNewChat, isNewChatWithSpace]);

  // Initialize speech recognition
  useEffect(() => {
    if ("webkitSpeechRecognition" in window) {
      recognitionRef.current = new window.webkitSpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-US";

      let silenceTimeout;

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setMicActive(true);
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";

        // Rebuild the full final transcript from index 0 every time, not just
        // event.resultIndex onward. With continuous=true, a natural pause
        // mid-dictation finalizes the current result and starts a new one —
        // each subsequent onresult event then has a resultIndex pointing past
        // the earlier segment(s). Starting from resultIndex (without a
        // persistent accumulator) dropped everything dictated before the
        // pause, since setPrompt() below replaces the whole textarea value.
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }

        if (finalTranscript) {
          setPrompt(finalTranscript);
        }

        // Reset silence timeout on every new result
        if (silenceTimeout) clearTimeout(silenceTimeout);
        silenceTimeout = setTimeout(() => {
          recognitionRef.current?.stop();
        }, 2000);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        setMicActive(false);
      };

      recognitionRef.current.onerror = (event) => {
        logger.error('Prompt', 'Speech recognition error', { error: event.error });
        setIsListening(false);
        setMicActive(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [setPrompt]);

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop the stream after getting permission
      return true;
    } catch (error) {
      logger.error('Prompt', 'Error requesting microphone permission', { error: error.message });
      return false;
    }
  };

  const toggleMic = async () => {
    if (prompt) {
      return;
    }

    if (!("webkitSpeechRecognition" in window)) {
      showFeedback("Speech recognition is not supported in this browser.", "warning");
      return;
    }

    try {
      if (!isListening) {
        const hasPermission = await requestMicrophonePermission();
        if (!hasPermission) {
          showFeedback(
            "Microphone permission is required for speech recognition.",
            "warning"
          );
          return;
        }
        recognitionRef.current?.start();
      } else {
        recognitionRef.current?.stop();
      }
    } catch (error) {
      logger.error('Prompt', 'Error toggling microphone', { error: error.message });
      setIsListening(false);
      setMicActive(false);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "24px";
      const maxHeight = window.innerHeight * 0.4;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${Math.max(24, newHeight)}px`;
    }
  };

  const updatePromptOverflow = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const hasOverflow = textarea.scrollHeight > textarea.clientHeight + 1;
    if (!hasOverflow) {
      setPromptOverflow({ top: false, bottom: false });
      return;
    }

    const top = textarea.scrollTop > 0;
    const bottom = textarea.scrollTop + textarea.clientHeight < textarea.scrollHeight - 1;
    setPromptOverflow({ top, bottom });
  };

  useEffect(() => {
    adjustTextareaHeight();
    requestAnimationFrame(updatePromptOverflow);
  }, [prompt]);

  // Pass user input through unchanged. Don't normalize newlines or truncate
  // mid-typing — silently rewriting the textarea is a hostile UX (the cursor
  // jumps and characters appear to vanish). Submit-time validation only.
  const handleInputChange = (e) => {
    setPrompt(e.target.value);
  };

  const realSend = async () => {
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt !== "" && !generateStatus && !fileLoading) {
      const compatibilityError = getFilesModelCompatibilityError(
        [...files, ...new_chat_files],
        selectedModel
      );
      if (compatibilityError) {
        showFeedback(compatibilityError, "error");
        return;
      }

      setGenerateStatus(true);

      if (isListening) {
        recognitionRef.current?.stop();
        setIsListening(false);
        setMicActive(false);
      }

      let savedChatId = null;

      try {
        // Save chat only if it's a genuinely new (not yet persisted) chat
        if (isNewChat) {
          savedChatId = await addChat(
            space,
            isNewChat,
            agent,
            chat,
            trimmedPrompt,
            chat_id
          );
          setTempChatId(null);
          // React Query will update via listener handlers
        }

        if (isNewChat || isNewChatWithSpace) {
          await uploadFiles(chat_id, user, orgId, space);
        }
        setPrompt(trimmedPrompt);
        const sent = await send(trimmedPrompt);
        if (sent === false) {
          setGenerateStatus(false);
          return;
        }
        setFiles([]);
      } catch (error) {
        // If chat was saved but sending failed, delete it from DB
        // React Query will handle UI updates via listeners
        if (savedChatId) {
          await deleteSpark(savedChatId);
        }
      }

      // Chat order is updated automatically via updated_at timestamp from backend
      if (textareaRef.current) {
        textareaRef.current.style.height = "24px";
      }
    }
  };

  const handleKeyDown = (e) => {
    // Check if device is desktop (width >= 768px, which is tablet/desktop breakpoint)
    const isDesktop = window.innerWidth >= 768;
    
    if (isDesktop && e.key === "Enter" && !e.shiftKey) {
      // Desktop: Enter key sends the message
      e.preventDefault();
      if (prompt.trim() && !generateStatus && !fileLoading) {
        realSend();
      }
    }
    // Shift+Enter on desktop will create a new line (default textarea behavior)
    // On mobile, no keyboard submission - only button works
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedFiles = [];

    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length > 0) {
      const dt = new DataTransfer();
      pastedFiles.forEach((file) => dt.items.add(file));
      fileInputRef.current.files = dt.files;

      // Set fileLoading immediately so the send button is disabled before
      // uploadFile's async incrementUploading can fire. Prevents a one-tick
      // window where the button appears enabled after a paste.
      FileStore.getState().incrementUploading();

      handleFileUpload(
        e,
        chat_id,
        fileInputRef,
        setFiles,
        files,
        user,
        orgId,
        messageLength,
        isNewChat,
        space,
        agent
      );

      // Balance the pre-emptive increment above. uploadFile already called
      // incrementUploading() internally, so the counter is now one ahead.
      // Decrement here to keep _uploadCount accurate — fileLoading stays true
      // as long as uploadFile's own increment hasn't been decremented yet.
      FileStore.getState().decrementUploading();
    }
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  if (generate_id === "QZ_START") return null;

  return (
    <>
      <ChatComposerLayout
        variant="page"
        className="chat-input-container max-w-[100vw] sm:max-w-4xl mx-auto mb-2 w-full px-4 sm:px-6 relative overflow-x-hidden"
      >
        <div className="h-[20px] pointer-events-none bottom-full prompt-linear-gradient absolute w-full" />
        <div
          className={`dark:bg-fill-navigation-surface-dark shadow-lg bg-fill-white border border-edge-gray-200 dark:border-transparent rounded-2xl px-3 sm:px-4 py-2 sm:py-3 opacity-100`}
        >
          {files?.length ? (
            <div className="flex items-center gap-[10px] mb-3 overflow-x-auto py-1 px-1 max-w-full">
              {files?.map((file) => (
                <FileUploadItem
                  key={file.name}
                  file={file}
                  onRemove={removeFile}
                  progress={file?.progress || 0}
                  isUploading={uploadingFiles[file.name]}
                />
              ))}
            </div>
          ) : null}
          {new_chat_files?.length ? (
            <div className="flex items-center gap-[10px] mb-3 overflow-x-auto py-1 px-1 max-w-full">
              {new_chat_files?.map((f) => (
                <FileUploadItem
                  key={f.file.name}
                  file={f.file}
                  onRemove={() => removeFile2(f.file__path)}
                  progress={f.file?.progress || 0}
                  isUploading={uploadingFiles[f.file.name]}
                  onNewChatScreen={true}
                />
              ))}
            </div>
          ) : null}
          {activeSkills.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              {activeSkills.map((skillId) => {
                const skill = availableSkills.find((s) => s.skill_id === skillId);
                return skill ? (
                  <span
                    key={skillId}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-fill-blue-50 dark:bg-fill-blue-900/30 text-ink-blue-600 dark:text-ink-blue-400 border border-edge-blue-200 dark:border-edge-blue-700"
                  >
                    <Wand2 size={10} />
                    {skill.display_name}
                    <button
                      onClick={() => toggleSkill(skillId)}
                      className="ml-0.5 hover:text-ink-blue-800 dark:hover:text-ink-blue-200"
                      aria-label={`Remove skill ${skill.name}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple // Add this attribute
            onChange={(e) => {
              handleFileUpload(
                e,
                chat_id,
                fileInputRef,
                setFiles,
                files,
                user,
                orgId,
                messageLength,
                isNewChat,
                space,
                agent
              );
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
              if (textareaRef.current) {
                textareaRef.current.focus();
              }
            }}
            accept={ALLOWED_FILE_ACCEPT_STRING}
            id="chatFileInput"
          />
          <div className="relative my-2">
            <div className="relative">
              <textarea
                id="prompt-textarea"
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                placeholder={
                  isListening
                    ? "Speak Now..."
                    : isNewDrag
                      ? "Drop here..."
                      : "Ask Anything..."
                }
                className="w-full bg-transparent placeholder-ink-copy-hover opacity-100 placeholder:italic px-3 focus:outline-none text-[15px] resize-none overflow-y-auto leading-[18px] dark:text-ink-white text-ink-ink dark:caret-ink-white caret-ink-ink"
                ref={textareaRef}
                value={prompt}
                onChange={handleInputChange}
                onScroll={updatePromptOverflow}
                aria-label="Prompt input"
                autoComplete="off"
              />
              {promptOverflow.top && (
                <div className="pointer-events-none absolute inset-x-0 top-0 h-3 rounded-t-[inherit] bg-gradient-to-b from-gradient-white/16 via-gradient-white/4 to-transparent backdrop-blur-[1px] dark:from-gradient-hex-25282f/16 dark:via-gradient-hex-25282f/4" />
              )}
              {promptOverflow.bottom && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3 rounded-b-[inherit] bg-gradient-to-t from-gradient-white/16 via-gradient-white/4 to-transparent backdrop-blur-[1px] dark:from-gradient-hex-25282f/16 dark:via-gradient-hex-25282f/4" />
              )}
            </div>
          </div>
          <div className="flex items-center justify-between gap-1 sm:gap-2 p-1 min-w-0">
            <div className="flex gap-1 sm:gap-2 items-center min-w-0">
              {/* Attach Dropdown */}
              <AttachDropdown
                fileInputRef={fileInputRef}
                filesLength={files.length}
              />
              {/* Skill Picker */}
              <PromptSkillSelector
                skills={availableSkills}
                selectedSkillIds={activeSkills}
                onToggle={toggleSkill}
              />
              {/* Model Selector */}
              <PromptStyleSelector />
              <PromptModelSelector compact />
            </div>
            <div className="flex items-center gap-1">
              {/* Device Selector — right side: monitor → globe → fire → send */}
              <PromptDeviceSelector chatId={chat_id} />
              <WebSearchToggle disabled={generateStatus || fileLoading} />
              {/* Spark Memory chip — sits next to mic/send */}
              <MemoryChip />
              {generate_id ? (
              <StopGenerationButton
                setGenerateStatus={setGenerateStatus}
                setRegenerateIndex={setRegenerateIndex}
              />
            ) : (
              <button
                type="button"
                onClick={
                  !generateStatus && !fileLoading
                    ? prompt && !isListening
                      ? realSend
                      : toggleMic
                    : null
                }
                aria-label={!isListening && prompt ? "Submit prompt" : "Hands free mode"}
                disabled={generateStatus || fileLoading}
                className={`transition-colors ${!isListening && prompt ? "photon-primary-action rounded-full" : ""} ${generateStatus || fileLoading
                  ? "opacity-50 cursor-not-allowed"
                  : prompt
                    ? isListening
                      ? "text-ink-editor-warning hover:text-ink-amber-300"
                      : "text-button-on-primary"
                    : isListening
                      ? "text-ink-red-400 animate-pulse"
                      : "text-ink-emerald-400 hover:text-ink-emerald-300"
                  }`}
              >
                {!isListening && prompt ? (
                  <div
                    className={`opacity-100 rounded-full w-9 h-9 flex items-center justify-center ${generateStatus || fileLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                      }`}
                    disabled={generateStatus || fileLoading}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </div>
                ) : (
                  <CustomTooltip content="Hands free mode">
                    <div className="group w-[36px] h-[36px] flex items-center justify-center hover:shadow-[0_0_0_0.5px_#6D808D4D] hover:bg-fill-navigation-hover hover:dark:bg-fill-navigation-hover-dark rounded-full cursor-pointer">
                      <div className="pointer-events-none">
                        {micActive ? (
                          <MicActive
                            size={20}
                            className="dark:fill-ink-white fill-ink-ink-alternate"
                          />
                        ) : (
                          <Mic
                            size={20}
                            className="dark:fill-ink-white fill-ink-ink-alternate"
                          />
                        )}
                      </div>
                    </div>
                  </CustomTooltip>
                )}
              </button>
            )}
            </div>
          </div>
        </div>
      </ChatComposerLayout>
    </>
  );
};

export default Prompt;